import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import type { JSX } from 'react';
import { FormProvider, type FieldValues } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PersonRecord } from '@/data/schemas';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';
import { useUnifiedForm } from '../../../../hooks/useUnifiedForm';
import {
  personnelFormFields,
  personnelFormSchema,
  type PersonnelFormValues,
} from '../../components/personnelFormSchema';
import type { Company, Site } from '@/data/schemas';
import { formatSiteSequence, formatSiteShorthand } from '@/utils/entityIdentity';
import {
  EntityDialog,
  EntityDialogFooter,
  EntityFormFields,
  FormErrorBanner,
  FormErrorSummary,
  useEntityDialogForm,
  useEntityDialogSubmit,
} from '@/components/blocks/forms';
import { Button, DialogClose } from '@/components/ui';
import { scrollToFirstError } from '@/components/blocks/forms/formErrors';
import { ConfirmDialog } from '@/components/blocks/feedback/ConfirmDialog';

interface PersonnelModalProps {
  personnel: PersonRecord | null;
  companyId: string;
  companyCode?: string | null;
  sites: Array<{ id: string; name: string; sequenceNumber?: number }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PersonnelModal({
  personnel,
  companyId,
  companyCode,
  sites,
  open,
  onOpenChange,
}: PersonnelModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const { contacts } = useDataProvider();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const companyStub: Pick<Company, 'code'> | null = useMemo(
    () => (companyCode ? { code: companyCode } : null),
    [companyCode]
  );
  const siteOptions = useMemo(
    () =>
      sites.map((site) => {
        const siteFragment: Pick<Site, 'sequenceNumber'> = {
          sequenceNumber: site.sequenceNumber ?? 0,
        };
        const shorthand = formatSiteShorthand(companyStub, siteFragment);
        const sequence = formatSiteSequence(site.sequenceNumber);
        return {
          value: site.id,
          label: shorthand
            ? `${site.name} (${shorthand})`
            : sequence
              ? `${site.name} (${sequence})`
              : site.name,
        };
      }),
    [companyStub, sites]
  );

  const fieldOverrides = useMemo(
    () => ({
      scope: {
        options: [
          { value: 'company', label: 'Azienda intera' },
          { value: 'site', label: 'Stabilimento specifico' },
        ],
      },
      siteId: {
        options: siteOptions,
        disabled: (values: Record<string, unknown>) =>
          values.scope !== 'site' || siteOptions.length === 0,
        dependsOn: ['scope'],
      },
      licenseExpiry: {
        disabled: (values: Record<string, unknown>) => !Boolean(values.licensed),
        dependsOn: ['licensed'],
      },
      licenseLastUpdate: {
        disabled: (values: Record<string, unknown>) => !Boolean(values.licensed),
        dependsOn: ['licensed'],
      },
    }),
    [siteOptions]
  );

  const { fields } = useUnifiedForm({
    schema: personnelFormSchema,
    fields: personnelFormFields,
    fieldOverrides,
    fieldOrder: [
      'firstName',
      'lastName',
      'role',
      'scope',
      'siteId',
      'email',
      'phone',
      'reportRecipient',
      'alarmContact',
      'reperibile',
      'fireTraining',
      'firstAidTraining',
      'licensed',
      'licenseExpiry',
      'licenseLastUpdate',
    ],
  });

  const updatePersonnel = useMutation({
    mutationFn: async (values: PersonnelFormValues) => {
      if (!personnel) throw new Error('Personnel not available');

      await contacts.update(companyId, personnel.id, mapPersonnelFormValues(values));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.detail(companyId),
        }),
      ]);
      toast.success('Personale aggiornato');
      onOpenChange(false);
    },
  });

  const deletePersonnel = useMutation({
    mutationFn: async () => {
      if (!personnel) throw new Error('Personnel not available');
      await contacts.remove(companyId, personnel.id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.detail(companyId),
        }),
      ]);
      toast.success('Personale archiviato');
      onOpenChange(false);
    },
  });

  const formData = useMemo(() => {
    if (!personnel) return null;
    return {
      firstName: personnel.firstName,
      lastName: personnel.lastName,
      email: personnel.email ?? undefined,
      phone: personnel.phone ?? undefined,
      role: personnel.role ?? '',
      scope: personnel.scope ?? 'company',
      siteId: personnel.scope === 'site' ? (personnel.siteId ?? undefined) : undefined,
      reportRecipient: personnel.reportRecipient,
      alarmContact: personnel.alarmContact,
      fireTraining: personnel.fireTraining,
      firstAidTraining: personnel.firstAidTraining,
      licensed: personnel.licensed,
      licenseExpiry: personnel.licenseExpiry ?? undefined,
      licenseLastUpdate: personnel.licenseLastUpdate ?? undefined,
      reperibile: personnel.reperibile,
    } satisfies PersonnelFormValues;
  }, [personnel]);

  const { form, formId, errorCount, rootError, handleInvalidSubmit, setRootError } =
    useEntityDialogForm({
      open,
      schema: personnelFormSchema,
      fields,
      data: formData ?? null,
    });

  const autosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAutosaveRef = useRef<string | null>(null);

  useEffect(() => {
    if (formData) {
      lastAutosaveRef.current = JSON.stringify(
        mapPersonnelFormValues(formData as PersonnelFormValues)
      );
    } else {
      lastAutosaveRef.current = null;
    }
  }, [formData]);

  const scheduleAutoSave = useCallback(
    (values: FieldValues) => {
      if (!open || !personnel) return;
      const payload = mapPersonnelFormValues(values as PersonnelFormValues);
      const snapshot = JSON.stringify(payload);
      if (snapshot === lastAutosaveRef.current) return;

      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
      autosaveTimeoutRef.current = setTimeout(async () => {
        try {
          await contacts.update(companyId, personnel.id, payload);
          lastAutosaveRef.current = snapshot;
        } catch {
          // Silent auto-save failures; explicit save still available.
        }
      }, 500);
    },
    [companyId, contacts, open, personnel]
  );

  useEffect(() => {
    if (!open || !personnel) return;
    const subscription = form.watch((values) => {
      scheduleAutoSave(values);
    });
    return () => {
      subscription.unsubscribe();
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [form, open, personnel, scheduleAutoSave]);

  const handleSubmit = useEntityDialogSubmit({
    form,
    onSubmit: async (values: FieldValues) => {
      await updatePersonnel.mutateAsync(values as PersonnelFormValues);
    },
    setRootError,
  });

  if (!personnel || !formData) {
    return <></>;
  }

  const handleArchive = async () => {
    try {
      await deletePersonnel.mutateAsync();
      setConfirmOpen(false);
    } catch (error) {
      setRootError('Impossibile archiviare. Riprova o contatta l’assistenza.');
      throw error;
    }
  };

  const isSaving = updatePersonnel.isPending;
  const isDeleting = deletePersonnel.isPending;
  const isDirty = form.formState.isDirty;

  const sections = [
    {
      title: 'Anagrafica',
      fields: ['firstName', 'lastName', 'role', 'email', 'phone'],
    },
    {
      title: 'Assegnazioni',
      fields: ['scope', 'siteId'],
    },
    {
      title: 'Abilitazioni',
      fields: [
        'reportRecipient',
        'alarmContact',
        'reperibile',
        'fireTraining',
        'firstAidTraining',
        'licensed',
        'licenseExpiry',
        'licenseLastUpdate',
      ],
    },
  ];

  return (
    <>
      <EntityDialog
        open={open}
        onOpenChange={onOpenChange}
        title={`Modifica ${personnel.firstName} ${personnel.lastName}`.trim()}
        size="sm"
        isDirty={isDirty}
        isSaving={isSaving}
        formId={formId}
        banner={rootError ? <FormErrorBanner message={rootError} /> : null}
        summary={
          errorCount > 0 ? (
            <FormErrorSummary
              count={errorCount}
              onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
            />
          ) : null
        }
        footer={
          <EntityDialogFooter
            extra={
              personnel ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setConfirmOpen(true)}
                  disabled={isSaving || isDeleting}
                >
                  {isDeleting ? 'Archiviazione…' : 'Archivia contatto'}
                </Button>
              ) : null
            }
            secondary={
              <DialogClose asChild>
                <Button variant="ghost" disabled={isSaving || isDeleting}>
                  Chiudi
                </Button>
              </DialogClose>
            }
            primary={
              <Button type="submit" form={formId} disabled={!isDirty || isSaving || isDeleting}>
                {isSaving ? 'Salvataggio…' : 'Salva'}
              </Button>
            }
          />
        }
      >
        <FormProvider {...form}>
          <form
            id={formId}
            onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
            noValidate
            className="space-y-4"
          >
            <EntityFormFields
              fields={fields}
              sections={sections}
              disabled={isSaving || isDeleting}
            />
          </form>
        </FormProvider>
      </EntityDialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Archiviare contatto?"
        message="L’elemento verrà archiviato e non sarà più visibile nelle liste."
        cancelLabel="Annulla"
        confirmLabel="Archivia"
        variant="danger"
        onConfirm={handleArchive}
        isLoading={isDeleting}
      />
    </>
  );
}

function mapPersonnelFormValues(values: PersonnelFormValues) {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone,
    role: values.role,
    scope: values.scope,
    siteId: values.scope === 'site' ? (values.siteId ?? undefined) : undefined,
    reportRecipient: values.reportRecipient,
    alarmContact: values.alarmContact,
    fireTraining: values.fireTraining,
    firstAidTraining: values.firstAidTraining,
    licensed: values.licensed,
    licenseExpiry: values.licenseExpiry,
    licenseLastUpdate: values.licenseLastUpdate,
    reperibile: values.reperibile,
  };
}
